// frontend/modules/template/components/ContextScriptEditor.tsx
import React, { useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { Spin } from 'antd';

interface ContextScriptEditorProps {
    value?: string;
    onChange?: (value: string) => void;
    height?: string;
    readOnly?: boolean;
    onValidationError?: (errorLine: number, errorMessage: string) => void;
    errorLine?: number;
    errorMessage?: string;
    language?: string;
}

export const ContextScriptEditor: React.FC<ContextScriptEditorProps> = ({
    value = '',
    onChange,
    height = '400px',
    readOnly = false,
    onValidationError,
    errorLine,
    errorMessage,
    language = 'javascript'
}) => {
    const editorRef = useRef<any>(null);
    const decorationsRef = useRef<string[]>([]);
    
    // Handle editor mounting
    const handleEditorDidMount = (editor: any, monaco: any) => {
        editorRef.current = editor;
        
        // Set editor options
        editor.updateOptions({
            tabSize: 2,
            minimap: { enabled: false },
            readOnly: readOnly
        });
        
        // Set up theme
        monaco.editor.defineTheme('customTheme', {
            base: 'vs',
            inherit: true,
            rules: [],
            colors: {
                'editor.background': '#FAFAFA',
            }
        });
        monaco.editor.setTheme('customTheme');
    };
    
    // Update error decorations when error line changes
    useEffect(() => {
        if (editorRef.current && errorLine && errorMessage) {
            const editor = editorRef.current;
            // @ts-ignore
            const monaco = window.monaco;
            
            // Clear previous decorations
            if (decorationsRef.current.length) {
                editor.deltaDecorations(decorationsRef.current, []);
            }
            
            // Add new decoration for error line
            decorationsRef.current = editor.deltaDecorations([], [
                {
                    range: new monaco.Range(errorLine, 1, errorLine, 1),
                    options: {
                        isWholeLine: true,
                        className: 'errorLineDecoration',
                        glyphMarginClassName: 'errorGlyphMargin',
                        glyphMarginHoverMessage: {
                            value: errorMessage
                        }
                    }
                }
            ]);
            
            // Position cursor at the error line
            editor.revealLineInCenter(errorLine);
            editor.setPosition({ lineNumber: errorLine, column: 1 });
            editor.focus();
        }
    }, [errorLine, errorMessage]);
    
    const handleEditorChange = (value: string | undefined) => {
        if (onChange && value !== undefined) {
            onChange(value);
            
            // Clear error highlighting when content changes
            if (errorLine && editorRef.current) {
                const editor = editorRef.current;
                if (decorationsRef.current.length) {
                    editor.deltaDecorations(decorationsRef.current, []);
                    decorationsRef.current = [];
                }
                
                if (onValidationError) {
                    onValidationError(0, '');
                }
            }
        }
    };
    
    return (
        <div className="context-script-editor-container">
            <Editor
                height={height}
                language={language}
                value={value}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
                loading={<Spin tip="Loading editor..." />}
                options={{
                    readOnly,
                    glyphMargin: true,
                    lineNumbers: 'on',
                    folding: true,
                    automaticLayout: true
                }}
            />
            <style>{`
                .errorLineDecoration {
                    background-color: #FFF1F0;
                    border-left: 3px solid #FF4D4F;
                }
                .errorGlyphMargin {
                    background-color: #FF4D4F;
                }
            `}</style>
        </div>
    );
};
